import './style.modules.scss'

import { Link, LinkVariant, Spacer } from '@ampcore/adel-react'
import { VideoDescription, VideoPlayer } from '@ampcore/video-player'
import React from 'react'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { MarketWatch } from '../MarketWatch/MarketWatch'
import type { TileListItem, TileListProps } from './TileList.type'

const renderHtml = (value?: string) => ({
  __html: rteContentFilter(value ?? '')
})

const hasVideo = (tile: TileListItem): boolean =>
  Boolean(tile.video?.playlistId?.trim())

const hasMarketWatch = (tile: TileListItem): boolean =>
  Boolean(tile.marketWatchProps)

const getTileModifierClass = (index: number): string => {
  switch (index) {
    case 0:
      return 'TileList-tile--first'
    case 1:
      return 'TileList-tile--second'
    case 2:
      return 'TileList-tile--third'
    default:
      return 'TileList-tile--default'
  }
}

export const TileList = (props: TileListProps) => {
  const {
    tiles,
    tileBackground = '',
    analyticsContainer = 'Taxes&Insights',
    componentSpacing,
    testId
  } = props
  return (
    <Spacer spacing={componentSpacing}>
      <section
        className="u-sizeConstrained"
        data-analytics-container={analyticsContainer}
        data-testid={testId ? `${testId}-section` : undefined}
      >
        <div
          className={`TileList TileList--${tileBackground} Grid Grid--withGutter Grid--fill u-sm-block u-md-block u-textPrimary`}
        >
          {tiles.map((tile, index) => {
            const tileHasVideo = hasVideo(tile)
            const tileClassName = [
              'Tile TileList-tile',
              getTileModifierClass(index),
              'u-margin8 u-paddingBottom16 u-textColorNeutral',
              tileHasVideo ? '' : 'u-paddingHoriz16 u-paddingVert24'
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <div
                className={tileClassName}
                key={`tile-${index + 1}`}
                data-testid={testId ? `${testId}-tile-${index}` : undefined}
              >
                {hasMarketWatch(tile) ? (
                  <>
                    {tile.superHeadline && (
                      <h5 className="Heading Heading--withSeparator u-textColorNeutral75">
                        <span
                          dangerouslySetInnerHTML={renderHtml(
                            tile.superHeadline
                          )}
                        />
                      </h5>
                    )}
                    {tile.marketWatchProps && (
                      <div
                        className="Tile-content u-marginTop8"
                        data-testid={
                          testId
                            ? `${testId}-market-watch-content-${index}`
                            : undefined
                        }
                      >
                        <MarketWatch
                          {...tile.marketWatchProps}
                          testId={
                            testId
                              ? `${testId}-market-watch-${index}`
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!tileHasVideo && tile.superHeadline && (
                      <h5 className="Heading Heading--withSeparator u-textColorNeutral75">
                        <span
                          dangerouslySetInnerHTML={renderHtml(
                            tile.superHeadline
                          )}
                        />
                      </h5>
                    )}
                    {!tileHasVideo && tile.headline && (
                      <h3>
                        <span
                          dangerouslySetInnerHTML={renderHtml(tile.headline)}
                        />
                      </h3>
                    )}
                    {!tileHasVideo && tile.content && (
                      <div
                        className="Tile-content u-marginTop8"
                        dangerouslySetInnerHTML={renderHtml(tile.content)}
                        data-testid={
                          testId ? `${testId}-content-${index}` : undefined
                        }
                      ></div>
                    )}
                    {tileHasVideo && (
                      <div className="Tile-content Tile-video u-paddingTop8 TileList-videoContainer">
                        <VideoPlayer
                          accountId={window.BRIGHTCOVE_ACC || ''}
                          policyKey={window.BRIGHTCOVE_PK || ''}
                          playlistId={tile.video?.playlistId}
                          testId={
                            testId ? `${testId}-video-${index}` : undefined
                          }
                        />
                        <VideoDescription
                          className="video-container Content"
                          accountId={window.BRIGHTCOVE_ACC || ''}
                          policyKey={window.BRIGHTCOVE_PK || ''}
                          playlistId={tile.video?.playlistId}
                          testId={
                            testId ? `${testId}-video-description` : undefined
                          }
                        />
                      </div>
                    )}
                    {tile.link?.name && tile.link?.url && (
                      <div
                        className={`${tileHasVideo ? 'u-paddingHoriz16 u-paddingBottom24' : 'u-marginTop40'}`}
                      >
                        <Link
                          variant={LinkVariant.SECONDARY}
                          name={tile.link?.name}
                          url={tile.link?.url}
                          target={tile.link?.target}
                          analyticsName={tile.link?.analyticsName}
                          testId={
                            testId ? `${testId}-button-${index}` : undefined
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </section>
    </Spacer>
  )
}
